import { createSlice, isAnyOf } from '@reduxjs/toolkit';
import MojitoCore from 'mojito/core';
import { isEmpty } from 'mojito/utils';
import userBonusesRetriever from './data-retriever';
import UserBonusesTypes from './types';
import { selectFreeBetBalance, selectCurrency } from 'services/user-info/selectors.js';
import { actions as userInfoActions } from 'services/user-info/slice.js';
import { actions as bootstrapActions } from 'services/bootstrap/slice.js';
import { actions as authenticationActions } from 'services/authentication/slice.js';
import betslipActionTypes from 'services/betslip/action-types.js';
import { isLoggedIn } from 'services/authentication/selectors.js';
import { selectFreeBetCode } from 'services/user-bonuses/selectors.js';
import ServicesTypes from 'services/common/types.js';

const reduxInstance = MojitoCore.Services.redux;
const { FREE_BETS_CODE_STATE, FREE_BETS_ERROR_CODE } = UserBonusesTypes;
const { UNKNOWN, AVAILABLE, UNAVAILABLE, PENDING } = ServicesTypes.CONTENT_STATE;

/**
 * The name of the user bonuses store. Will be used to register in global redux store.
 *
 * @constant
 * @type {string}
 * @memberof Mojito.Services.UserBonuses
 */
export const STORE_KEY = 'userBonusesStore';

/**
 * Defines the state of the user bonuses store.
 *
 * @typedef UserBonusesState
 *
 * @property {Mojito.Services.UserBonuses.types.FreeBet[]} activeFreeBets - Stored active free bets.
 * @property {Mojito.Services.UserBonuses.types.FreeBet[]} historicFreeBets - Stored historic free bets bonus information.
 * @property {Mojito.Services.UserBonuses.types.OddsBoost[]} activeOddsBoosts - Stored active odds boosts.
 * @property {Mojito.Services.UserBonuses.types.OddsBoost[]} historicOddsBoosts - Stored historic odds boosts information.
 * @property {Mojito.Services.UserBonuses.types.FREE_BETS_CODE_STATE} freeBetCodeState - Free bets code state.
 * @property {Mojito.Services.Bonus.types.FreeBetError} freeBetCodeError - Free bet code error.
 * @property {object} freeBetsBalance - Freebets balance.
 * @property {string} freeBetCode - Free bet code id.
 * @property {Mojito.Services.Common.types.CONTENT_STATE} activeBonusesLoadingState - Active bonuses loading state.
 * @property {Mojito.Services.Common.types.CONTENT_STATE} historicBonusesLoadingState - Historic bonuses loading state.
 *
 * @memberof Mojito.Services.UserBonuses
 */

export const initialState = {
    activeFreeBets: [],
    activeOddsBoosts: [],
    historicFreeBets: [],
    historicOddsBoosts: [],
    freeBetCodeState: FREE_BETS_CODE_STATE.READY,
    freeBetCodeError: undefined,
    freeBetsBalance: undefined,
    freeBetCode: '',
    activeBonusesLoadingState: UNKNOWN,
    historicBonusesLoadingState: UNKNOWN,
};

export const { reducer, actions } = createSlice({
    name: 'userBonuses',
    initialState: initialState,
    reducers: {
        fetchActiveUserBonusesSuccess(state, { payload: { freebets, oddsBoosts } }) {
            state.activeBonusesLoadingState = AVAILABLE;
            state.activeFreeBets = freebets;
            state.activeOddsBoosts = oddsBoosts;
        },
        fetchActiveUserBonusesPending(state) {
            state.activeBonusesLoadingState = PENDING;
        },
        fetchActiveUserBonusesFailed(state) {
            state.activeBonusesLoadingState = UNAVAILABLE;
        },
        fetchHistoricUserBonusesSuccess(state, { payload: { freebets, oddsBoosts } }) {
            state.historicBonusesLoadingState = AVAILABLE;
            state.historicFreeBets = freebets;
            state.historicOddsBoosts = oddsBoosts;
        },
        fetchActiveFreeBetsSuccess(state, { payload: { freebets } }) {
            state.activeFreeBets = freebets;
        },
        fetchHistoricUserBonusesPending(state) {
            state.historicBonusesLoadingState = PENDING;
        },
        fetchHistoricUserBonusesFailure(state) {
            state.historicBonusesLoadingState = UNAVAILABLE;
        },
        inputFreeBet(state, { payload: freeBetCode }) {
            state.freeBetCode = freeBetCode;
            state.freeBetCodeState = FREE_BETS_CODE_STATE.READY;
            state.freeBetCodeError = undefined;
        },
        addFreeBetSuccess(state) {
            state.freeBetCodeState = FREE_BETS_CODE_STATE.ACCEPTED;
            state.freeBetCode = '';
        },
        addFreeBetPending(state) {
            state.freeBetCodeState = FREE_BETS_CODE_STATE.PENDING;
        },
        addFreeBetEmpty(state) {
            state.freeBetCodeState = FREE_BETS_CODE_STATE.ERROR;
            state.freeBetCodeError = { type: FREE_BETS_ERROR_CODE.EMPTY_INPUT };
        },
        addFreeBetFailure(state, { payload }) {
            const { messages, type } = payload;
            state.freeBetCodeState = FREE_BETS_CODE_STATE.ERROR;
            state.freeBetCodeError = { type, message: messages ? messages[0] : undefined };
        },
        updateFreeBetsBalance(state, { payload: freeBetBalance }) {
            state.freeBetsBalance = freeBetBalance;
        },
        dispose(state) {
            state.activeFreeBets = [];
            state.activeOddsBoosts = [];
            state.historicFreeBets = [];
            state.historicOddsBoosts = [];
            state.freeBetCodeState = FREE_BETS_CODE_STATE.READY;
            state.freeBetCodeError = undefined;
            state.freeBetsBalance = undefined;
            state.activeBonusesLoadingState = UNKNOWN;
            state.historicBonusesLoadingState = UNKNOWN;
        },
    },
});

actions.fetchHistoricUserBonuses = () => (dispatch, getState) => {
    if (isLoggedIn(getState())) {
        dispatch(actions.fetchHistoricUserBonusesPending());
        userBonusesRetriever.getHistoricUserBonuses(selectCurrency(getState()));
    }
};

actions.addFreeBet = () => (dispatch, getState) => {
    const { freeBetCode } = getState()[STORE_KEY];
    if (!freeBetCode) {
        dispatch(actions.addFreeBetEmpty());
    } else {
        dispatch(actions.addFreeBetPending());
        userBonusesRetriever.addFreeBet(selectFreeBetCode(getState()));
    }
};

actions.fetchActiveFreeBets = () => (dispatch, getState) => {
    if (isLoggedIn(getState())) {
        if (userBonusesRetriever.hasTaskRunner()) {
            userBonusesRetriever.fetchFreeBets();
        } else {
            userBonusesRetriever.getActiveFreeBets(selectCurrency(getState()));
        }
    }
};

actions.fetchActiveUserBonuses = () => (dispatch, getState) => {
    if (isLoggedIn(getState())) {
        dispatch(actions.fetchActiveUserBonusesPending());
        userBonusesRetriever.getActiveUserBonuses(selectCurrency(getState()));
    }
};

reduxInstance.actionListener.startListening({
    matcher: isAnyOf(bootstrapActions.dispose, authenticationActions.disposeSession),
    effect: (action, listenerApi) => {
        listenerApi.dispatch(actions.dispose());
        userBonusesRetriever.stopPollingFreeBets();
    },
});

reduxInstance.actionListener.startListening({
    actionCreator: authenticationActions.loginSuccess,
    effect: (_, listenerApi) => {
        const state = listenerApi.getState();
        userBonusesRetriever.pollFreeBets(selectCurrency(state));
    },
});

reduxInstance.actionListener.startListening({
    type: betslipActionTypes.placeBetslipSuccess,
    effect: ({ payload = {} }, listenerApi) => {
        const { receipt } = payload;
        // Check for receipt since placeBetslipSuccess is triggered when going to overask.
        if (!isEmpty(receipt)) {
            listenerApi.dispatch(actions.fetchActiveFreeBets());
        }
    },
});

reduxInstance.actionListener.startListening({
    actionCreator: userInfoActions.updateBalance,
    effect: (_, listenerApi) => {
        const state = listenerApi.getState();
        const userFreeBetBalance = selectFreeBetBalance(state);
        if (state[STORE_KEY].freeBetsBalance !== userFreeBetBalance) {
            listenerApi.dispatch(actions.updateFreeBetsBalance(userFreeBetBalance));
            listenerApi.dispatch(actions.fetchActiveFreeBets());
        }
    },
});

/**
 * User bonuses related actions.
 *
 * @module UserBonusesActions
 * @name actions
 * @memberof Mojito.Services.UserBonuses
 */

/**
 * Retrieve active user bonuses information.
 *
 * @function fetchActiveUserBonuses
 * @returns {Mojito.Core.Services.redux.ThunkFunction} Fetch active user bonuses Thunk.
 *
 * @memberof Mojito.Services.UserBonuses.actions
 */

/**
 * Retrieve active free bets bonus information.
 *
 * @function fetchActiveFreeBets
 * @returns {Mojito.Core.Services.redux.ThunkFunction} Fetch active free bets Thunk.
 *
 * @memberof Mojito.Services.UserBonuses.actions
 */

/**
 * Retrieve historic user bonuses information.
 *
 * @function fetchHistoricUserBonuses
 * @returns {Mojito.Core.Services.redux.ThunkFunction} Fetch historic user bonuses Thunk.
 *
 * @memberof Mojito.Services.UserBonuses.actions
 */

/**
 * Bonus historic user bonuses retrieved successfully.
 *
 * @function fetchHistoricUserBonusesSuccess
 * @type {Mojito.Core.Services.redux.ActionCreator}
 * @param {Mojito.Services.Bonus.types.UserBonuses[]} userBonuses - List of free bets and odds boosts objects.
 *
 * @memberof Mojito.Services.UserBonuses.actions
 */

/**
 * Active user bonuses retrieved successfully.
 *
 * @function fetchActiveUserBonusesSuccess
 * @returns {Mojito.Core.Services.redux.ThunkFunction} Thunk.
 * @param {Mojito.Services.Bonus.types.UserBonuses[]} userBonuses - List of free bets and odds boosts objects.
 *
 * @memberof Mojito.Services.UserBonuses.actions
 */

/**
 * Fetch active user bonuses pending.
 *
 * @function fetchActiveUserBonusesPending
 * @type {Mojito.Core.Services.redux.ActionCreator}
 *
 * @memberof Mojito.Services.UserBonuses.actions
 */

/**
 * Fetch active user bonuses failed.
 *
 * @function fetchActiveUserBonusesFailed
 * @type {Mojito.Core.Services.redux.ActionCreator}
 *
 * @memberof Mojito.Services.UserBonuses.actions
 */

/**
 * User bonuses retrieval failed.
 *
 * @function fetchHistoricUserBonusesFailure
 * @type {Mojito.Core.Services.redux.ActionCreator}
 * @param {Mojito.Core.Services.Transactions.types.Error} error - Error.
 *
 * @memberof Mojito.Services.UserBonuses.actions
 */

/**
 * Bonus active free bets retrieved successfully.
 *
 * @function fetchActiveFreeBetsSuccess
 * @type {Mojito.Core.Services.redux.ActionCreator}
 * @param {Mojito.Services.Bonus.types.FreeBet[]} freeBets - List of free bets.
 *
 * @memberof Mojito.Services.UserBonuses.actions
 */

/**
 * Action to add a free bet voucher to the user.
 *
 * @function addFreeBet
 * @returns {Mojito.Core.Services.redux.ThunkFunction} Thunk.
 *
 * @memberof Mojito.Services.UserBonuses.actions
 */

/**
 * Update successful free bet added state.
 *
 * @function addFreeBetSuccess
 * @type {Mojito.Core.Services.redux.ActionCreator}
 *
 * @memberof Mojito.Services.UserBonuses.actions
 */

/**
 * Failed to add free bet code.
 *
 * @function addFreeBetFailure
 * @type {Mojito.Core.Services.redux.ActionCreator}
 * @param {Mojito.Core.Services.Transactions.types.Error} error - Error.
 *
 * @memberof Mojito.Services.UserBonuses.actions
 */

/**
 * Free bet code input action. Typically, arrives from user input interaction.
 *
 * @function inputFreeBet
 * @type {Mojito.Core.Services.redux.ActionCreator}
 * @param {string} code - Code.
 *
 * @memberof Mojito.Services.UserBonuses.actions
 */

/**
 * Update free bets balance.
 *
 * @function updateFreeBetsBalance
 * @type {Mojito.Core.Services.redux.ActionCreator}
 * @param {string|undefined} freeBetBalance - FreeBet balance.
 *
 * @memberof Mojito.Services.UserBonuses.actions
 */

reduxInstance.injectReducer(STORE_KEY, reducer);
